/**
 * Les formes que le portail rend.
 *
 * Écrites à la main plutôt qu'engendrées depuis OpenAPI : le schéma existe
 * (`/api/openapi.json`) et pourrait les produire, mais l'engendrement
 * imposerait une étape de plus à chaque construction pour une surface de
 * vingt-neuf routes. Le jour où elle double, il vaudra la peine.
 */

export interface Identite {
  user_id: string;
  tenant_id: string;
  platform_role: string;
  nom?: string;
  courriel?: string;
  /** L'assistant natif (`0015`) — fermé, l'onglet Assistant rend l'iframe
   *  Chainlit, le repli de la transition. */
  assistant_natif?: boolean;
}

/** Le relevé des sources d'une affaire prête.
 *
 * **Nullable, et c'est le contrat.** L'API ne le calcule que sur une affaire
 * prête ; archivée, elle rend `null`. Le typer non nullable a coûté une page
 * blanche : `{}` passait la garde `!completude` et cassait au premier champ. */
export interface Completude {
  cctp: number;
  articles_indexes: number;
  plans_dwg: number;
  plans_dxf: number;
  modele: boolean;
  missions: number;
  signalements: number;
  detenteur_du_classeur: string;
  manque: { titre: string; raison: string }[];
}

export interface AffaireVue {
  affaire_id: string;
  reference: string;
  label: string | null;
  state: string;
  workspace_key: string;
  role: string | null;
  version: number;
  modifiee_le: string;
  /** Le nombre de pièces par catégorie, les vides omises. */
  sources?: Record<string, number>;
  /** Le relevé des sources — **absent hors de la liste, nul si non prête**. */
  completude?: Completude | null;
  /** Le dossier racine OneDrive, si l'affaire en a un : il ouvre le Démarrage. */
  racine?: DossierRacine | null;
  /** Le profil d'architecte auquel l'affaire est affectée, ou `null`. */
  profil?: Profil | null;
}

/** Un profil d'architecte de l'espace de travail (hors affaire). */
export interface Profil {
  profil_id: string;
  nom: string;
  cabinet: string;
  contact: string;
  courriel: string;
  telephone: string;
  notes: string;
  cree_le: string;
  modifie_le: string;
  /** Nombre d'affaires affectées (présent dans la liste). */
  affaires?: number;
}

/** Le détail d'un profil : ses affaires et le glossaire consolidé du bureau. */
export interface ProfilDetail {
  profil: Profil;
  affaires: {
    affaire_id: string;
    reference: string;
    label: string | null;
    state: string;
    glossaire: boolean;
  }[];
  avec_glossaire: number;
  /** Le glossaire fusionné de toutes les affaires du profil, ou `null`. */
  glossaire: RapportGlossaire | null;
  /** Le catalogue d'ouvrages du bureau — les colonnes de métré réutilisées
   *  d'une affaire à l'autre. `[]` tant qu'aucun n'est posé. */
  catalogue: EntreeCatalogue[];
}

/** Une ligne du catalogue d'ouvrages d'un bureau (jamais un nombre). */
export interface EntreeCatalogue {
  clef: string;
  libelle: string;
  unite: string;
  groupe: string;
}

/** Un fichier de l'audit, avec sa proposition et ce que l'utilisateur en a fait. */
export interface FichierAudit {
  id: string;
  nom: string;
  chemin: string;
  extension: string;
  octets: number;
  /** La catégorie proposée par la flotte (immuable). */
  categorie_proposee: string | null;
  /** Le choix de l'utilisateur ; `null` = on s'en remet à la proposition. */
  categorie_retenue: string | null;
  /** La catégorie effective (retenue, sinon proposée ; `null` = hors périmètre). */
  categorie: string | null;
  confiance: string;
  motif: string;
  etiquettes: string[];
  commentaire: string;
  /** L'état d'import de la pièce : "PRET" (vert) | "EN_COURS" | "ECHEC" | ""
   *  (jamais importé, rouge). */
  importe: string;
}

/** Une carte de catégorie de l'audit — nom, notes, et ses fichiers. */
export interface CategorieAudit {
  clef: string;
  libelle: string;
  /** Nom propre à l'affaire ; `""` = employer le libellé. */
  nom: string;
  etiquettes: string[];
  commentaire: string;
  presents: number;
  /** Combien des fichiers de la carte sont déjà importés (état PRET). */
  importes: number;
  fichiers: FichierAudit[];
}

/** Le compte rendu d'un lot d'import lancé depuis l'audit. */
export interface CompteImport {
  demandes: number;
  deposes: number;
  deja: number;
  refus: { nom: string; raison: string }[];
}

/** Une entrée de l'historique des audits — un audit passé, daté. */
export interface HistoriqueAudit {
  report_id: string;
  commence_le: string;
  etat: string;
  fichiers_vus: number;
  classes: number;
  /** Le nombre de catégories requises manquantes à cet audit. */
  manque: number;
  /** L'audit était complet (terminé, rien ne manque). */
  complet: boolean;
}

/** Un fichier apparu ou disparu entre deux audits. */
export interface DiffFichier {
  nom: string;
  chemin: string;
  categorie: string;
}

/** Un fichier qui a changé de catégorie entre deux audits. */
export interface DiffDeplace {
  nom: string;
  chemin: string;
  de: string;
  vers: string;
}

/** L'écart d'effectif d'une catégorie entre deux audits. */
export interface DiffCategorie {
  clef: string;
  libelle: string;
  avant: number;
  apres: number;
  delta: number;
}

/** Ce qui a changé d'un rapport passé au rapport courant. */
export interface DiffAudit {
  nouveaux: DiffFichier[];
  disparus: DiffFichier[];
  deplaces: DiffDeplace[];
  par_categorie: DiffCategorie[];
  resume: { nouveaux: number; disparus: number; deplaces: number; inchanges: number };
}

/** Un rapport d'audit, recomposé depuis les fichiers curés — `null` tant
 *  qu'aucun audit n'a tourné. Le plus récent est éditable (`courant`) ; un
 *  rapport passé se relit en lecture seule, avec un diff vers le courant. */
export interface RapportAudit {
  job_id: string;
  /** L'état du travail : QUEUED | PROCESSING | READY | FAILED | CANCELED. */
  etat: string;
  motif: string;
  detail: string;
  commence_le: string;
  modifie_le: string;
  fichiers_vus: number;
  dossiers_vus: number;
  classes: number;
  tronque: boolean;
  categories: CategorieAudit[];
  manque: { titre: string; raison: string }[];
  ecartes: FichierAudit[];
  /** L'identifiant de ce rapport, et s'il est le dernier (donc éditable). */
  report_id: string;
  courant: boolean;
  /** Les audits passés, du plus récent au plus ancien. */
  historique: HistoriqueAudit[];
  /** Ce qui a changé jusqu'au courant — présent seulement en consultant un
   *  rapport passé, `null` sur le courant. */
  diff: DiffAudit | null;
}

/** Ce que le modèle propose pour préremplir un champ de note — un seul des
 *  trois est renseigné, selon le champ demandé (`nom`|`etiquettes`|`commentaire`). */
export interface SuggestionNote {
  nom?: string;
  etiquettes?: string[];
  commentaire?: string;
}

/** Un fichier d'une grappe de doublons, avec le sort que la flotte propose. */
export interface FichierDoublon {
  id: string;
  nom: string;
  chemin: string;
  octets: number;
  modifie_le: string;
  /** À conserver (sinon : proposé au retrait). */
  garder: boolean;
  raison: string;
}

/** Un ensemble de fichiers jugés être le même document — doublon ou révision. */
export interface GrappeDoublons {
  /** "doublon" (exemplaires identiques) | "revision" (versions successives). */
  type: string;
  motif: string;
  fichiers: FichierDoublon[];
}

/** Le repérage des doublons d'une carte — vide tant qu'il n'y a rien à retirer. */
export interface RapportDoublons {
  categorie: string;
  libelle: string;
  grappes: GrappeDoublons[];
  /** Trop de grappes : l'analyse s'est arrêtée, toutes n'ont pas été pesées. */
  tronque: boolean;
}

export interface Piece {
  document_id: string;
  categorie: string;
  nom: string;
  dossier: string;
  etat: string;
  modifie_le: string;
  /** La taille de la dernière révision descendue ; `null` avant. */
  octets?: number | null;
  /** Combien de fois la pièce a changé depuis son rattachement. */
  revisions?: number;
}

export interface Travail {
  job_id: string;
  genre: string;
  etat: string;
  progression: number;
  tentatives: number;
  motif: string;
  detail: string;
  nom: string | null;
  categorie: string | null;
  depuis: string;
  modifie_le: string;
}

/** Une étape de préparation (conversion, index, catalogue) et son bilan. */
export interface EtapePreparation {
  nom: string;
  requis: boolean;
  fait: boolean;
  detail: string;
  compte: number;
  avertissements: string[];
}

/** Le compte rendu d'une préparation, conservé sur l'instantané. Pendant
 *  l'exécution, `en_cours` nomme l'étape qui tourne et `etapes_prevues` la liste
 *  complète — de quoi suivre l'avancée en direct. */
export interface RapportPreparation {
  reussi: boolean;
  avertissements: string[];
  etapes: EtapePreparation[];
  /** Toutes les étapes attendues, dans l'ordre (présent dès qu'une préparation
   *  a démarré). */
  etapes_prevues?: string[];
  /** L'étape en cours d'exécution, ou `null`/absent une fois terminé. */
  en_cours?: string | null;
}

export interface Instantane {
  jeu_id: string;
  generation: number;
  etat: string;
  pieces: number;
  active_le: string | null;
  cree_le: string;
  /** Le compte rendu de la dernière préparation, ou `null` si jamais préparé. */
  rapport?: RapportPreparation | null;
}

/** Où une entrée du glossaire paraît : une planche, combien de fois, et — pour
 *  un symbole — sur quels calques et à quels points (en mètres). */
export interface EmplacementGlossaire {
  source: string;
  occurrences?: number;
  calques?: string[];
  points?: number[][];
}

/** Une entrée du glossaire : un symbole (avec image), un repère, une légende ou
 *  une unité. La description est un AVIS de la flotte (elle décrit, ne mesure
 *  pas), avec sa confiance. */
export interface EntreeGlossaire {
  famille: string;
  nom: string;
  description: string;
  occurrences: number;
  emplacements: EmplacementGlossaire[];
  /** L'image vectorielle de référence (SVG brut), `null` hors symbole ou si le
   *  bloc ne se rend pas. */
  image_svg: string | null;
  source: string;
  confiance: string;
  motif: string;
  donnees: Record<string, unknown>;
}

/** Le résumé agrégé d'un glossaire : comptes, familles, unités, alertes. */
export interface ResumeGlossaire {
  feuilles?: number;
  symboles?: number;
  menuiseries?: number;
  reperages?: number;
  legendes?: number;
  calques?: number;
  lots?: number;
  nomenclatures_pdf?: number;
  familles?: Record<string, number>;
  /** D'où vient le sens d'un symbole : « dxf », « dxf+pdf », « dxf+cctp »… */
  provenances?: Record<string, number>;
  unites?: { unite: string; planches: number }[];
  alertes?: string[];
}

/** Le glossaire le plus récent d'une affaire, avec l'état de son travail. `null`
 *  — jamais `{}` — tant qu'aucun inventaire n'a tourné. */
export interface RapportGlossaire {
  job_id: string;
  etat: string;
  /** Avancement de l'inventaire, 0–100 (le même que celui du tiroir Traitements). */
  progression?: number;
  /** Temps estimé avant la fin, en secondes — `null` hors inventaire en cours ou
   *  tant qu'il est trop tôt pour l'estimer. Mêle l'historique de l'affaire et
   *  l'avancement direct (voir `glossaire._projeter`). */
  restant_s?: number | null;
  /** Durée totale estimée de l'inventaire en cours, en secondes (ou `null`). */
  total_s?: number | null;
  generation: number | null;
  feuilles: number;
  compte: {
    symboles: number; reperages: number; legendes: number;
    menuiseries: number; calques: number; lots?: number;
  };
  resume: ResumeGlossaire;
  symboles: EntreeGlossaire[];
  /** Les nomenclatures de menuiseries (portes int./ext., fenêtres), par type —
   *  enrichies de leurs lignes PDF et de leur CCTP ; y compris les nomenclatures
   *  lues au PDF seul (`donnees.pdf_seule`). */
  menuiseries: EntreeGlossaire[];
  reperages: EntreeGlossaire[];
  legendes: EntreeGlossaire[];
  /** La nomenclature des calques dessinés — la « légende de facto » du dossier. */
  calques: EntreeGlossaire[];
  unites: EntreeGlossaire[];
  /** Les lots du CCTP indexé — le squelette normatif du DCE (whole-DCE). */
  lots?: EntreeGlossaire[];
  motif: string;
  detail: string;
  commence_le: string;
  modifie_le: string;
}

/** Un niveau reconstructible de l'instantané actif (onglet Reconstruction). */
export interface NiveauReconstruction {
  niveau: string;
  titre: string;
}

export interface Mission {
  run_id: string;
  mission_id: string;
  etat: string;
  generation: number | null;
  ouverte_le: string;
  modifiee_le: string;
}

export interface Connexion {
  connexion_id: string;
  compte: string;
  sante: string;
  utilisable: boolean;
}

/** Un élément de OneDrive, **réduit à ce qu'un écran affiche**. */
export interface Element {
  drive_id: string;
  item_id: string;
  nom: string;
  octets: number;
  dossier: boolean;
  modifie_le: string;
}

export interface Cran {
  nom: string;
  chemin: string;
}

export interface Parcours {
  donnees?: never;
  elements: Element[];
  suite: string | null;
  fil: Cran[];
}

/** Un dossier OneDrive retenu comme racine d'une affaire.
 *
 * `chemin` le nomme pour l'œil et pour le serveur (la route `enfants` parcourt
 * par chemin) ; `item_id`/`drive_id` en donnent l'identité durable, insensible
 * au renommage. Les deux sont conservés — l'un lisible, l'autre stable. */
export interface DossierRacine {
  connexion_id: string;
  drive_id: string;
  item_id: string;
  chemin: string;
  nom: string;
}

/** Les six catégories de pièces. `M12` — DPGF et cadre restent distincts. */
export const CATEGORIES = [
  { clef: "CCTP", libelle: "CCTP", extensions: ".pdf" },
  { clef: "PLAN_DWG", libelle: "Plans DWG", extensions: ".dwg" },
  { clef: "PLAN_PDF", libelle: "Plans PDF", extensions: ".pdf" },
  { clef: "PLAN_IFC", libelle: "Maquette IFC", extensions: ".ifc, .ifczip" },
  { clef: "DPGF", libelle: "DPGF", extensions: ".csv, .xls, .xlsx" },
  { clef: "METRE_TEMPLATE", libelle: "Cadre de sortie client", extensions: ".xlsx, .xlsm" },
] as const;

export type Categorie = (typeof CATEGORIES)[number]["clef"];
